import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import ApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { DataGrid } from '@/components';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import Select, { MultiValue } from 'react-select';
import { BookingType, GroupByUnit, UserRoles } from '@/constant';
import { HarborCapability } from '@/constant/harbour';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useCurrentUserRoles } from '@/utils/userRole';
import {
  useLazyExportHarborFinanceCsvQuery,
  useGetHarborRevenueTimeSeriesQuery,
  useGetHarborUsageQuery,
  useGetHarborTopItemsQuery,
} from '@/services/HarborFinanceReportsService';
import type {
  HarborFinanceFilters,
  HarborRevenueTimeSeriesItem,
  HarborTopServiceItem,
  HarborTopBerthItem,
  HarborTopAddonItem,
} from '@/types/harbor-finance-reports.types';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate, formatDisplayDateRange } from '@/utils/Date';
import { usePermissions } from '@/hooks';

// Using centralized Finnish currency formatter

type SelectOption = { value: string; label: string };

export default function HarborFinanceReports({
  harborId,
}: {
  harborId?: string;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { hasCapability } = usePlanPermissions();
  const { hasPermission } = usePermissions();
  const roles = useCurrentUserRoles();
  const isAdmin = roles.includes(UserRoles.ADMIN);
  const mainRef = useRef<HTMLElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportCsv, { isLoading: exportLoading }] =
    useLazyExportHarborFinanceCsvQuery();

  // Scaffold filters for future use (already wired into queries)
  const groupByOptions: SelectOption[] = [
    { value: GroupByUnit.DAY, label: 'Daily' },
    { value: GroupByUnit.WEEK, label: 'Weekly' },
    { value: GroupByUnit.MONTH, label: 'Monthly' },
  ];
  const [selectedGroupBy, setSelectedGroupBy] = useState<SelectOption | null>(
    groupByOptions[2]
  );
  const bookingTypeOptions: SelectOption[] = [
    { value: BookingType.BERTH, label: 'Berth' },
    { value: BookingType.ADDON, label: 'Add-on (Standalone)' },
    { value: BookingType.SERVICE, label: 'Service' },
  ];
  const [selectedBookingTypes, setSelectedBookingTypes] = useState<
    SelectOption[]
  >([]);
  const currentYear = new Date().getFullYear();
  const yearOptions: SelectOption[] = Array.from({ length: 5 }, (_, index) => {
    const year = currentYear - index;
    return { value: String(year), label: String(year) };
  });
  const [selectedYear, setSelectedYear] = useState<SelectOption | null>(
    yearOptions[0]
  );
  // Date range filter (DD/MM/YYYY) like CancellationReports
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  const filters: HarborFinanceFilters | undefined = useMemo(() => {
    if (!harborId) return undefined;

    let from: string | undefined;
    let to: string | undefined;

    const hasCustomStart =
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
    const hasCustomEnd =
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();

    if (hasCustomStart && hasCustomEnd) {
      from = moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      to = moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    }

    const year = selectedYear ? Number(selectedYear.value) : currentYear;

    return {
      harbor_id: harborId,
      groupBy: selectedGroupBy?.value,
      booking_types: selectedBookingTypes.length
        ? selectedBookingTypes.map((o) => o.value)
        : undefined,
      from,
      to,
      year,
    };
  }, [
    harborId,
    selectedGroupBy,
    selectedBookingTypes,
    tempStartDate,
    tempEndDate,
    selectedYear,
    currentYear,
  ]);

  const canExportCsv =
    isAdmin || (hasPermission('finance_reporting_export') && hasCapability(HarborCapability.BOOKINGS_REPORTS_EXPORT_CSV));

  const exportFileName = useMemo(() => {
    if (!filters) {
      return `harbor_finance_report_${new Date().toISOString().slice(0, 10)}.csv`;
    }

    if (filters.from && filters.to) {
      return `harbor_finance_report_${filters.from}_${filters.to}.csv`;
    }

    if (filters.year) {
      return `harbor_finance_report_${filters.year}.csv`;
    }

    return `harbor_finance_report_${new Date().toISOString().slice(0, 10)}.csv`;
  }, [filters]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  };

  const resetFilters = () => {
    setSelectedGroupBy(groupByOptions[2]);
    setSelectedBookingTypes([]);
    setTempStartDate('');
    setTempEndDate('');
    setSelectedYear(yearOptions[0]);
  };

  const handleExportCsv = async () => {
    if (!filters || !harborId) return;

    try {
      const blob = await exportCsv(filters).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', exportFileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Finance report CSV exported successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to export finance report CSV:', error);
      enqueueSnackbar('Failed to export finance report CSV', {
        variant: 'error',
      });
    }
  };

  const {
    data: ts,
    isLoading: tsLoading,
    isError: tsError,
  } = useGetHarborRevenueTimeSeriesQuery(filters as HarborFinanceFilters, {
    skip: !filters,
    refetchOnMountOrArgChange: true,
  });
  const { data: usage, isError: usageError } = useGetHarborUsageQuery(
    filters as HarborFinanceFilters,
    {
      skip: !filters,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: topItems, isError: topError } = useGetHarborTopItemsQuery(
    filters as HarborFinanceFilters,
    {
      skip: !filters,
      refetchOnMountOrArgChange: true,
    }
  );

  const timeSeries = useMemo<HarborRevenueTimeSeriesItem[]>(
    () => (Array.isArray(ts) ? (ts as HarborRevenueTimeSeriesItem[]) : []),
    [ts]
  );
  const usageItems = useMemo(() => usage?.items || [], [usage]);
  const services = useMemo<HarborTopServiceItem[]>(
    () => topItems?.services || [],
    [topItems]
  );
  const berths = useMemo<HarborTopBerthItem[]>(
    () => topItems?.berths || [],
    [topItems]
  );
  const addons = useMemo<HarborTopAddonItem[]>(
    () => topItems?.addons || [],
    [topItems]
  );

  // Aggregate cards
  const totals = useMemo(() => {
    const gross = timeSeries.reduce((s, r) => s + (r.gross_amount || 0), 0);
    const refunded = timeSeries.reduce(
      (s, r) => s + (r.total_refunded || 0),
      0
    );
    const net = timeSeries.reduce((s, r) => s + (r.net_revenue || 0), 0);
    const totalBookings = usageItems.reduce(
      (s, u) => s + (u.total_bookings || 0),
      0
    );
    const totalNights = usageItems.reduce(
      (s, u) => s + (u.total_booking_nights || 0),
      0
    );
    return { gross, refunded, net, totalBookings, totalNights };
  }, [timeSeries, usageItems]);

  // Charts
  const area = useMemo(() => {
    const periods = timeSeries.map((r) => r.period).sort();
    const categories = periods.map((p) => formatDisplayDate(new Date(p)));
    const netSeries = periods.map((p) =>
      timeSeries
        .filter((r) => r.period === p)
        .reduce((s, r) => s + (r.net_revenue || 0), 0)
    );

    const series: ApexOptions['series'] = [
      { name: 'Net Revenue', data: netSeries },
    ];
    const options: ApexOptions = {
      chart: { type: 'area', height: 300, toolbar: { show: false } },
      dataLabels: { enabled: false },
      legend: { show: true },
      stroke: { curve: 'smooth', show: true, width: 3 },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '12px' } },
      },
      yaxis: {
        labels: {
          formatter: (val) => Number(val).toFixed(2),
          style: { colors: 'var(--tw-gray-500)', fontSize: '12px' },
        },
      },
      tooltip: {
        y: { formatter: (val) => Number(val).toFixed(2) },
      },
      fill: { gradient: { opacityFrom: 0.25, opacityTo: 0 } },
      grid: {
        borderColor: 'var(--tw-gray-200)',
        strokeDashArray: 5,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
    };
    return { series, options };
  }, [timeSeries]);

  const breakdownArea = useMemo(() => {
    const periods = Array.from(new Set(timeSeries.map((r) => r.period))).sort();
    const categories = periods.map((p) => formatDisplayDate(new Date(p)));

    const berthSeries = periods.map((p) =>
      timeSeries
        .filter((r) => r.period === p)
        .reduce((s, r) => s + (r.berth_net_revenue || 0), 0)
    );
    const addonSeries = periods.map((p) =>
      timeSeries
        .filter((r) => r.period === p)
        .reduce((s, r) => s + (r.addon_net_revenue || 0), 0)
    );
    const serviceSeries = periods.map((p) =>
      timeSeries
        .filter((r) => r.period === p)
        .reduce((s, r) => s + (r.service_net_revenue || 0), 0)
    );

    const hasData =
      berthSeries.some((v) => v > 0) ||
      addonSeries.some((v) => v > 0) ||
      serviceSeries.some((v) => v > 0);

    const series: ApexOptions['series'] = [
      { name: 'Berth Net Revenue', data: berthSeries },
      { name: 'Add-on Net Revenue', data: addonSeries },
      { name: 'Service Net Revenue', data: serviceSeries },
    ];

    const options: ApexOptions = {
      chart: { type: 'area', height: 320, toolbar: { show: false } },
      dataLabels: { enabled: false },
      legend: { show: true },
      stroke: { curve: 'smooth', show: true, width: 3 },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '12px' } },
      },
      yaxis: {
        labels: {
          formatter: (val) => Number(val).toFixed(2),
          style: { colors: 'var(--tw-gray-500)', fontSize: '12px' },
        },
      },
      tooltip: { y: { formatter: (val) => Number(val).toFixed(2) } },
      fill: { gradient: { opacityFrom: 0.25, opacityTo: 0 } },
      grid: {
        borderColor: 'var(--tw-gray-200)',
        strokeDashArray: 5,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
    };

    return { series, options, hasData };
  }, [timeSeries]);

  const berthDonut = useMemo(() => {
    const labels = berths.map((b) => b.berth_number);
    const series = berths.map((b) => Number(b.net_revenue || 0));
    const options: ApexOptions = {
      chart: { type: 'donut', height: 320, toolbar: { show: false } },
      labels,
      legend: { position: 'bottom' },
      dataLabels: { enabled: true },
      tooltip: {
        y: { formatter: (val) => formatFinnishCurrency(Number(val)) },
      },
      stroke: { show: false },
      plotOptions: { pie: { donut: { size: '60%' } } },
    };
    return { series, options };
  }, [berths]);

  const addonDonut = useMemo(() => {
    const labels = addons.map((a) => a.addon_name);
    const series = addons.map((a) => Number(a.revenue || 0));
    const options: ApexOptions = {
      chart: { type: 'donut', height: 320, toolbar: { show: false } },
      labels,
      legend: { position: 'bottom' },
      dataLabels: { enabled: true },
      tooltip: {
        y: { formatter: (val) => formatFinnishCurrency(Number(val)) },
      },
      stroke: { show: false },
      plotOptions: { pie: { donut: { size: '60%' } } },
    };
    return { series, options };
  }, [addons]);

  const serviceDonut = useMemo(() => {
    const labels = services.map((s) => s.service_name || '-');
    const series = services.map((s) => Number(s.net_revenue || 0));
    const options: ApexOptions = {
      chart: { type: 'donut', height: 320, toolbar: { show: false } },
      labels,
      legend: { position: 'bottom' },
      dataLabels: { enabled: true },
      tooltip: {
        y: { formatter: (val) => formatFinnishCurrency(Number(val)) },
      },
      stroke: { show: false },
      plotOptions: { pie: { donut: { size: '60%' } } },
    };
    return { series, options };
  }, [services]);

  // Tables
  const tsColumns = [
    {
      id: 'period',
      header: 'Period',
      accessorFn: (row: HarborRevenueTimeSeriesItem) =>
        row.period_range
          ? formatDisplayDateRange(row.period_range)
          : formatDisplayDate(new Date(row.period)),
      enableSorting: false,
    },
    {
      id: 'gross',
      header: 'Gross Amount',
      accessorFn: (row: HarborRevenueTimeSeriesItem) =>
        formatFinnishCurrency(row.gross_amount || 0),
      enableSorting: false,
    },
    {
      id: 'refunded',
      header: 'Refunded',
      accessorFn: (row: HarborRevenueTimeSeriesItem) =>
        formatFinnishCurrency(row.total_refunded || 0),
      enableSorting: false,
    },
    {
      id: 'net',
      header: 'Net Revenue',
      accessorFn: (row: HarborRevenueTimeSeriesItem) =>
        formatFinnishCurrency(row.net_revenue || 0),
      enableSorting: false,
    },
  ];

  // Error notification (once per change)
  useEffect(() => {
    if (tsError || usageError || topError) {
      enqueueSnackbar('Failed to fetch finance reports', { variant: 'error' });
    }
  }, [tsError, usageError, topError, enqueueSnackbar]);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;

    const cleanupHandlers: Array<{
      el: HTMLElement;
      handler: (e: KeyboardEvent) => void;
    }> = [];

    const applyLegendKeyboardAccess = () => {
      const legends = Array.from(
        root.querySelectorAll<HTMLElement>('.apexcharts-legend')
      );

      legends.forEach((legend) => {
        legend.setAttribute('tabindex', '0');
        legend.setAttribute('role', 'region');
        if (!legend.getAttribute('aria-label')) {
          legend.setAttribute('aria-label', 'Chart legend');
        }

        const handler = (e: KeyboardEvent) => {
          const scrollAmount = 40;
          const pageAmount = Math.max(
            legend.clientHeight - scrollAmount,
            scrollAmount
          );

          switch (e.key) {
            case 'ArrowDown':
              legend.scrollTop += scrollAmount;
              e.preventDefault();
              break;
            case 'ArrowUp':
              legend.scrollTop -= scrollAmount;
              e.preventDefault();
              break;
            case 'PageDown':
              legend.scrollTop += pageAmount;
              e.preventDefault();
              break;
            case 'PageUp':
              legend.scrollTop -= pageAmount;
              e.preventDefault();
              break;
            case 'Home':
              legend.scrollTop = 0;
              e.preventDefault();
              break;
            case 'End':
              legend.scrollTop = legend.scrollHeight;
              e.preventDefault();
              break;
            default:
              break;
          }
        };

        legend.addEventListener('keydown', handler);
        cleanupHandlers.push({ el: legend, handler });
      });
    };

    const rafId = requestAnimationFrame(() => {
      applyLegendKeyboardAccess();
    });

    return () => {
      cancelAnimationFrame(rafId);
      cleanupHandlers.forEach(({ el, handler }) => {
        el.removeEventListener('keydown', handler);
      });
    };
  }, [timeSeries.length, berths.length, services.length, addons.length]);

  return (
    <main ref={mainRef} className="harbor-finance-reports">
      <div className="harbor-finance-header flex flex-wrap items-center gap-3 lg:gap-4 justify-between mb-6">
        <div className="harbor-finance-header-left flex items-center">
          <h1 className="text-xl font-semibold hidden lg:block">
            Finance Reports
          </h1>
        </div>
        <div className="harbor-finance-header-right flex flex-wrap gap-3 sm:gap-4 justify-start sm:justify-end w-full lg:w-auto">
          <div className="min-w-[160px]">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Group by"
              value={selectedGroupBy}
              options={groupByOptions}
              onChange={(val) => {
                setSelectedGroupBy(val as SelectOption);
              }}
              aria-label="Group by"
            />
          </div>
          <div className="min-w-[120px]">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Year"
              value={selectedYear}
              options={yearOptions}
              onChange={(val) => {
                setSelectedYear(val as SelectOption);
              }}
              aria-label="Year"
            />
          </div>
          <div className="min-w-[200px]">
            <Select
              isMulti
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Booking Types"
              options={bookingTypeOptions}
              value={selectedBookingTypes}
              onChange={(vals: MultiValue<SelectOption>) =>
                setSelectedBookingTypes(vals as SelectOption[])
              }
              aria-label="Booking Types"
            />
          </div>
          <div className="flex-shrink-0">
            <DateRangePicker
              startDateName="from"
              endDateName="to"
              isMandatory={false}
              value={[tempStartDate, tempEndDate]}
              onChange={([newStartDate, newEndDate]) => {
                handleDateRangeChange(
                  newStartDate as string,
                  newEndDate as string
                );
              }}
              allowPastDates={true}
              allowFutureDates={false}
              className="w-full sm:w-64 h-8 sm:h-10 text-sm"
            />
          </div>
          {(selectedGroupBy?.value !== GroupByUnit.MONTH ||
            selectedBookingTypes.length > 0 ||
            !!tempStartDate ||
            !!tempEndDate ||
            (selectedYear &&
              selectedYear.value !== yearOptions[0].value)) && (
              <div className="flex items-center">
                <button
                  type="button"
                  className="btn btn-sm btn-light flex items-center gap-2"
                  onClick={resetFilters}
                  title="Clear filters"
                >
                  Clear Filters
                </button>
              </div>
            )}
          {canExportCsv ? (
            <div className="flex items-center">
              <button
                type="button"
                className={`btn btn-sm btn-primary flex items-center justify-center gap-2 ${exportLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                onClick={handleExportCsv}
                disabled={exportLoading || !harborId}
              >
                {exportLoading ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <div className="text-sm text-gray-500 contrast:text-white/90">
              Gross Amount
            </div>
            <div className="text-2xl font-semibold contrast:text-white/80">
              {formatFinnishCurrency(totals.gross)}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="text-sm text-gray-500 contrast:text-white/90">
              Refunded
            </div>
            <div className="text-2xl font-semibold contrast:text-white/80">
              {formatFinnishCurrency(totals.refunded)}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="text-sm text-gray-500 contrast:text-white/90">
              Net Revenue
            </div>
            <div className="text-2xl font-semibold contrast:text-white/80">
              {formatFinnishCurrency(totals.net)}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="card">
          <div className="card-body" ref={chartRef}>
            <h2 className="text-base font-semibold mb-4 contrast:text-white/90">
              Net Revenue
            </h2>
            <ApexChart
              id="harbor_finance_area"
              options={area.options}
              series={area.series}
              type="area"
              width="100%"
              height="300"
            />
          </div>
        </div>
      </div>

      {breakdownArea.hasData ? (
        <div className="mb-6">
          <div className="card">
            <div className="card-body">
              <h2 className="text-base font-semibold mb-4 contrast:text-white/90">
                Revenue Breakdown (Net)
              </h2>
              <ApexChart
                id="harbor_revenue_breakdown_area"
                options={breakdownArea.options}
                series={breakdownArea.series}
                type="area"
                width="100%"
                height="300"
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {berths.length ? (
          <div className="card">
            <div className="card-body">
              <h2 className="text-base font-semibold mb-4 contrast:text-white/90">
                Top Berths (Net Revenue)
              </h2>
              <div className="mx-auto w-full max-w-[480px]">
                <ApexChart
                  id="harbor_berths_donut"
                  options={berthDonut.options}
                  series={berthDonut.series as number[]}
                  type="donut"
                  width="100%"
                  height="320"
                />
              </div>
            </div>
          </div>
        ) : null}
        {services.length ? (
          <div className="card">
            <div className="card-body">
              <h2 className="text-base font-semibold mb-4 contrast:text-white/90">
                Top Services (Net Revenue)
              </h2>
              <div className="mx-auto w-full max-w-[480px]">
                <ApexChart
                  id="harbor_services_donut"
                  options={serviceDonut.options}
                  series={serviceDonut.series as number[]}
                  type="donut"
                  width="100%"
                  height="320"
                />
              </div>
            </div>
          </div>
        ) : null}
        {addons.length ? (
          <div className="card">
            <div className="card-body">
              <h2 className="text-base font-semibold mb-4 contrast:text-white/90">
                Top Add-ons (Revenue)
              </h2>
              <div className="mx-auto w-full max-w-[480px]">
                <ApexChart
                  id="harbor_addons_donut"
                  options={addonDonut.options}
                  series={addonDonut.series as number[]}
                  type="donut"
                  width="100%"
                  height="320"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="card">
          <div className="card-body">
            <h2 className="text-base font-semibold mb-4 contrast:text-white/90">
              Revenue by Period
            </h2>
            <div className="finance-grid-wrapper">
              <DataGrid
                columns={tsColumns}
                data={timeSeries}
                rowSelect={false}
                loading={tsLoading}
                pagination={{
                  page: 0,
                  size: 10,
                  more: false,
                  totalItems: timeSeries.length,
                  totalPages: Math.max(
                    1,
                    Math.ceil((timeSeries.length || 0) / 10)
                  ),
                  setPagination: () => undefined,
                  sizes: [10, 25, 50],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                  info: '{from} - {to} of {count}',
                }}
                serverSide={false}
                aria-label="Revenue time series table"
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
